<script setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import QcFindingsScreen from '@/components/evaluation/qc/QcFindingsScreen.vue'
import QcNotice from '@/components/evaluation/qc/QcNotice.vue'
import { useQcRunSummary } from '@/composables/useQcRunSummary'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

/**
 * RED-389: the REdata admin reads one appraiser's quality check, read-only,
 * opened from `Aruande kontroll → Tagasiside` or `Kasutus ja kulu`.
 *
 * A thin page over the appraiser's `QcFindingsScreen` in `readOnly` mode: no
 * resolution button, no undo, no feedback row, no file card, no start or
 * re-run, no polling. Everything here only reads. The server has already
 * stripped what the admin may not see (the file, the lines around a quote,
 * the appraisers' feedback).
 */
const props = defineProps({
  /** The admin page part the admin came from, for the back link. */
  from: { type: String, default: 'tagasiside' },
  address: { type: String, default: null },
  appraiser: { type: String, default: null },
  company: { type: String, default: null },
  supportEmail: { type: String, required: true },
  check: { type: Object, required: true },
})

const { t } = useI18n()

const findings = computed(() => props.check.findings ?? [])
const checklist = computed(() => props.check.checklist ?? [])

const { isPartial, partialBodyKey, partialEmptyBodyKey, standardsNotice, checkedItemCount, costLine } = useQcRunSummary(
  () => props.check,
  checklist,
)

const isRunning = computed(() => ['queued', 'processing'].includes(props.check.status))
const isFailed = computed(() => props.check.status === 'failed')
const isFinished = computed(() => props.check.status === 'completed')

const backLabel = computed(() =>
  props.from === 'kasutus'
    ? t('messages.admin.report_qc.checkView.backKasutus')
    : t('messages.admin.report_qc.checkView.backTagasiside'),
)

const checkedAt = computed(() => {
  const raw = props.check.finishedAt ?? props.check.createdAt
  const at = raw ? new Date(raw) : null
  if (!at || Number.isNaN(at.getTime())) {
    return null
  }
  return `${at.toLocaleDateString('et-EE')} ${at.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' })}`
})

const metaLine = computed(() => [props.appraiser, props.company, checkedAt.value].filter(Boolean).join(' · '))
</script>

<template>
  <div class="py-6" data-testid="report-qc-check-view" :data-status="check.status">
    <!-- Back / up: top-left, quiet, breadcrumb style (CLAUDE.md UI rules). -->
    <Link
      :href="route('admin.report-qc.index', { part: from })"
      class="mb-3 inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-800"
      data-testid="report-qc-check-back"
    >
      <i class="pi pi-arrow-left text-xs" aria-hidden="true" />
      <span>{{ backLabel }}</span>
    </Link>

    <div class="mb-4">
      <p class="text-xs font-medium uppercase tracking-wider text-gray-400">{{ t('messages.admin.report_qc.checkView.title') }}</p>
      <h1 class="text-xl font-medium" data-testid="report-qc-check-address">
        {{ address ?? t('messages.admin.report_qc.checkView.noAddress') }}
      </h1>
      <p v-if="metaLine" class="mt-1 text-sm text-gray-500" data-testid="report-qc-check-meta">{{ metaLine }}</p>
    </div>

    <QcNotice tone="info" icon="pi-eye" class="mb-4 shadow-xs" data-testid="report-qc-check-readonly">
      {{ appraiser
        ? t('messages.admin.report_qc.checkView.readOnly', { appraiser })
        : t('messages.admin.report_qc.checkView.readOnlyNoName') }}
    </QcNotice>

    <QcNotice v-if="isRunning" tone="info" icon="pi-clock" class="mb-4 shadow-xs" data-testid="report-qc-check-running">
      {{ t('messages.admin.report_qc.checkView.running') }}
    </QcNotice>

    <QcNotice v-if="isFailed" tone="warn" icon="pi-exclamation-triangle" class="mb-4 shadow-xs" data-testid="report-qc-check-failed">
      <strong class="font-semibold text-gray-700">{{ t('messages.admin.report_qc.checkView.failed') }}</strong>
      <template v-if="check.errorMessage"> {{ check.errorMessage }}</template>
    </QcNotice>

    <template v-if="isFinished">
      <QcNotice
        v-if="isPartial && findings.length"
        tone="warn"
        icon="pi-exclamation-triangle"
        class="mb-4 shadow-xs"
        data-testid="qc-partial"
        :data-stop-reason="check.stopReason ?? ''"
      >
        <strong class="font-semibold text-gray-700">{{ t('messages.evaluation.qualityCheck.partial.title') }}</strong>
        {{ t(partialBodyKey, { done: check.checksDone, total: check.checksTotal }) }}
      </QcNotice>
      <QcNotice
        v-if="standardsNotice"
        tone="warn"
        icon="pi-book"
        class="mb-4 shadow-xs"
        data-testid="qc-standards-missing"
      >
        <strong class="font-semibold text-gray-700">{{ t('messages.evaluation.qualityCheck.standards.missingTitle') }}</strong>
        {{ standardsNotice }}
      </QcNotice>

      <QcFindingsScreen
        v-if="findings.length"
        :findings="findings"
        :checklist="checklist"
        :checks-total="checkedItemCount"
        :support-email="supportEmail"
        read-only
      />
      <div v-else class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-empty">
        <p v-if="isPartial" class="text-sm text-gray-700" data-testid="qc-empty-partial">
          <strong class="font-semibold">{{ t('messages.evaluation.qualityCheck.partial.title') }}</strong>
          {{ t(partialEmptyBodyKey, { done: check.checksDone, total: check.checksTotal }) }}
        </p>
        <p v-else class="text-sm text-gray-700">
          <strong class="font-semibold text-green-800">{{ t('messages.evaluation.qualityCheck.empty.title') }}</strong>
          {{ t('messages.evaluation.qualityCheck.empty.checked', { items: checkedItemCount }) }}
        </p>
      </div>
    </template>

    <p v-if="costLine" class="mt-6 text-xs text-gray-400" data-testid="qc-admin-cost">
      {{ costLine }} {{ t('messages.evaluation.qualityCheck.cost.adminOnly') }}
    </p>
  </div>
</template>
